import { supabase } from '../utils/supabase';
import { fetchPlayerRecentSessions } from './playerService';
import { fetchSessionLeaderboardSnapshotRecap } from './leaderboardSnapshotService';
import { fetchPlayerActivityFeed } from './activityFeedService';
import { fetchPlayerTopMatchups } from './matchupService';
import { buildSessionLadderSnapshot, summarizeLadderGameResults } from '../utils/sessionSnapshotUtils';
import { MIXED_LEVELS_GAME_TYPE_IDS, winRatePctFromRecord } from '../utils/winRate';

const GAME_HISTORY_PAGE_SIZE = 900;

/**
 * Mixed and levels win rate for the sessions shown on the club page.
 * Reads only those session ids from `player_game_history`.
 *
 * @param {string[]} sessionIds
 * @returns {Promise<Map<string, { winRatePct: number|null, gamesPlayed: number, wins: number, losses: number }>>}
 */
export async function fetchClubPlayerSessionWinRates(playerId, clubId, sessionIds = []) {
  const ids = [...new Set((sessionIds || []).filter(Boolean))];
  if (!playerId || !clubId || !ids.length) return new Map();

  const totals = new Map(ids.map((id) => [id, { wins: 0, played: 0 }]));

  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('player_game_history')
      .select('session_id, result')
      .eq('player_id', playerId)
      .eq('club_id', clubId)
      .in('session_id', ids)
      .in('game_type_id', MIXED_LEVELS_GAME_TYPE_IDS)
      .range(offset, offset + GAME_HISTORY_PAGE_SIZE - 1);

    if (error) throw error;

    const batch = data || [];
    for (const row of batch) {
      const bucket = totals.get(row.session_id);
      if (!bucket) continue;
      bucket.played += 1;
      if (row.result === 'win') bucket.wins += 1;
    }

    if (batch.length < GAME_HISTORY_PAGE_SIZE) break;
    offset += GAME_HISTORY_PAGE_SIZE;
  }

  const map = new Map();
  for (const [sessionId, { wins, played }] of totals) {
    if (played <= 0) continue;
    map.set(sessionId, {
      winRatePct: winRatePctFromRecord(wins, played),
      gamesPlayed: played,
      wins,
      losses: played - wins,
    });
  }
  return map;
}

async function countBalancedGames({ playerId, clubId, seasonId, result }) {
  let query = supabase
    .from('player_game_history')
    .select('game_id', { count: 'exact', head: true })
    .eq('player_id', playerId)
    .eq('club_id', clubId)
    .in('game_type_id', MIXED_LEVELS_GAME_TYPE_IDS);

  if (seasonId) query = query.eq('season_id', seasonId);
  if (result) query = query.eq('result', result);

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

/**
 * Mixed and levels record at a club. Pass seasonId to scope to that season.
 * @returns {Promise<{ wins: number, gamesPlayed: number, winRatePct: number|null }|null>}
 */
export async function fetchClubBalancedRecord(playerId, clubId, seasonId = null) {
  if (!playerId || !clubId) return null;

  const [gamesPlayed, wins] = await Promise.all([
    countBalancedGames({ playerId, clubId, seasonId }),
    countBalancedGames({ playerId, clubId, seasonId, result: 'win' }),
  ]);

  return {
    wins,
    gamesPlayed,
    winRatePct: winRatePctFromRecord(wins, gamesPlayed),
  };
}

/**
 * Returns the player's most recent session at a specific club, enriched with
 * leaderboard snapshot recap data (rank delta, ELO delta, W/L per ladder).
 *
 * Returns null if the player has no recorded games at this club.
 */
export async function fetchClubLastSessionSnapshot(playerId, clubId, viewerGender) {
  if (!playerId || !clubId) return null;

  const allRecent = await fetchPlayerRecentSessions(playerId, 20, { includeEloDetails: true });
  if (!allRecent || allRecent.length === 0) return null;

  const clubSession = allRecent.find((s) => s.clubId === clubId);
  if (!clubSession) return null;

  const { sessionId, sessionName, sessionDate, gamesPlayed, wins, losses, mixedEloDelta, levelsEloDelta } = clubSession;

  const [snapshotRecap, sgData] = await Promise.all([
    fetchSessionLeaderboardSnapshotRecap(sessionId, playerId, clubId).catch(() => []),
    supabase
      .from('session_games')
      .select('id, created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .then(({ data }) => data || []),
  ]);

  let mixedResults = [];
  let levelsResults = [];

  if (sgData.length > 0) {
    const sgIds = sgData.map((g) => g.id);
    const sgCreatedAt = new Map(sgData.map((g) => [g.id, g.created_at]));
    const { data: psgData } = await supabase
      .from('players_session_games')
      .select('session_game_id, result, pair_type')
      .eq('player_id', playerId)
      .in('session_game_id', sgIds);

    const sorted = (psgData || []).slice().sort((a, b) => {
      const ta = sgCreatedAt.get(a.session_game_id) || '';
      const tb = sgCreatedAt.get(b.session_game_id) || '';
      return ta < tb ? -1 : ta > tb ? 1 : 0;
    });
    for (const row of sorted) {
      if (row.pair_type === 'levels') levelsResults.push(row.result);
      else mixedResults.push(row.result);
    }
  }

  const winRatePct = gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : null;

  const ladderSnapshot = buildSessionLadderSnapshot({
    mixedStats: summarizeLadderGameResults(mixedResults),
    levelsStats: summarizeLadderGameResults(levelsResults),
    mixedEloDelta: mixedEloDelta || 0,
    levelsEloDelta: levelsEloDelta || 0,
    snapshotRecap,
    viewerGender,
  });

  return {
    sessionId,
    sessionName,
    sessionDate,
    gamesPlayed,
    wins,
    losses,
    winRatePct,
    snapshotRecap,
    ladderSnapshot,
  };
}

/**
 * Returns the player's most recent activity moments filtered to a single club.
 * Client-side filter — no DB migration required for v1.
 */
export async function fetchClubMoments(clubId, limit = 10) {
  if (!clubId) return [];
  const all = await fetchPlayerActivityFeed({ limit: 50 }).catch(() => []);
  return all.filter((e) => e.club_id === clubId).slice(0, limit);
}

/**
 * Returns the player's top matchups filtered to a single club.
 * Client-side filter on get_player_top_matchups — no DB migration required for v1.
 */
export async function fetchClubTopMatchups(clubId, limit = 5) {
  if (!clubId) return [];
  const all = await fetchPlayerTopMatchups({ limit: 20 }).catch(() => []);
  return all.filter((m) => m.club_id === clubId).slice(0, limit);
}

/**
 * All-time mixed and levels win rate at a club. Same figure as the club card.
 * Returns null if no recorded games.
 */
export async function fetchClubWinRate(playerId, clubId) {
  try {
    const record = await fetchClubBalancedRecord(playerId, clubId);
    return record?.winRatePct ?? null;
  } catch (error) {
    console.warn('fetchClubWinRate:', error);
    return null;
  }
}

